import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { api, ApiError } from "../api";
import { useCan, useSession } from "../auth/session";
import { Card, Dialog, EmptyState, Field, InlineAlert, PageHeader, PageShell, StatusChip, icons, useToast } from "../company-ui";
import { Tip } from "../help/Tip";
import { dotLayout } from "./dots";
import "./estimate.css";

/**
 * Team estimates, organizer side (ALN-47, docs/pages/estimate.md B "Team estimates (organizer)"),
 * laid out as the mockup's v31/v33 estimate view: the Surveys list on the left; the selected survey's
 * head and its Results / Set up and send tabs on the right; the Teams dialog from the page head.
 * Estimators (surveys.answer only) get "Your surveys". Nothing here shows money: results and the apply
 * preview are hours.
 */

type Size = "S" | "M" | "L";
const SIZES: Size[] = ["S", "M", "L"];
const SIZE_NAME: Record<Size, string> = { S: "Small", M: "Medium", L: "Large" };
const STAGE_NAME: Record<string, string> = { sourcing: "Sourcing", farming: "Farming", receiving: "Receiving", crush_press: "Crush and press", fermentation: "Fermentation", aging: "Aging", blending: "Blending", bottling: "Bottling", warehouse: "Warehouse" };
const STAGE_ORDER = Object.keys(STAGE_NAME);
const STATUS: Record<string, { label: string; tone: "info" | "neutral" | "success" | "pending" }> = { draft: { label: "Draft", tone: "neutral" }, open: { label: "Open", tone: "info" }, closed: { label: "Closed", tone: "neutral" }, applied: { label: "Applied", tone: "success" } };

interface TeamRef { id: string; name: string; archived: boolean }
interface ListDto { surveys: { id: string; name: string; team: TeamRef; status: string; rounds: number; invited: number; answered: number; closesOn: string; nextReminder: string | null }[]; unassignedRounds: number; roundsTotal: number; today: string }
interface RoundView { id: string; title: string; question: string | null; countsNote: string | null; stage: string | null; activityKey: string | null; hoursPer: "job" | "month"; bands: Record<Size, { range: string }> | null; sizesMissing: boolean; bandsChanged: boolean; status: string }
interface PersonDto { id: string; name: string; email: string | null; userId: string | null; link: string | null; sentAt: string | null; printedAt: string | null; bounced: string | null }
interface SurveyDto {
  id: string; name: string; team: TeamRef; intro: string; status: "draft" | "open" | "closed" | "applied"; closesOn: string; sentAt: string | null; closedAt: string | null; appliedAt: string | null; closedOn: string | null;
  remindersEnabled: boolean; revision: number; rounds: RoundView[]; invited: number; answered: number; emailed: number; nextReminder: string | null; today: string;
  failed: { count: number; of: number }; bounced: { linkId: string; address: string; message: string }[]; people?: PersonDto[]; availableRounds: RoundView[];
}
interface BandResult { range: string; answers: number; notSure: number; hours: number[]; median: number | null; min: number | null; max: number | null; wideSpread: boolean; outliers: number[] }
interface ResultsDto { rounds: { id: string; activity: string; hoursPer: "job" | "month"; bandsChanged: boolean; status: string; bands: Record<Size, BandResult>; appliedTo: { kind: string; id: string; name: string; on: string }[] }[]; notes: { roundId: string; body: string }[] }
interface TeamDto { id: string; name: string; archivedAt: string | null; surveys: number; version: number }
interface TargetsDto { templates: { id: string; name: string }[]; programs: { id: string; name: string; status: string }[] }
interface PreviewRow { roundId: string; activity: string; size?: Size; median?: number; hoursBefore?: number | null; hoursAfter?: number; volume?: { qty: number; unit: string }; line?: { id: string; role: string }; rows?: { id: string; role: string; hours: number | null }[]; skipped?: string }
interface PreviewDto { target: { kind: string; id: string; name: string }; rows: PreviewRow[]; previewRevision: number }

const NETWORK = "That change wasn't saved. Check the connection and try again.";
const errText = (e: unknown) => (e instanceof ApiError ? e.message : NETWORK);
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
const short = (d: string | null) => (d ? new Date(`${d.slice(0, 10)}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" }) : "");
const h1 = (n: number) => n.toFixed(1);
/** "12 tons", "2,715 gallons": the target's volume in the round's size unit (B, "is Medium for this activity (12 tons)"). */
const volumeText = (v: { qty: number; unit: string }) => `${v.qty.toLocaleString("en-US", { maximumFractionDigits: 1 })} ${v.qty === 1 ? v.unit.replace(/s$/, "") : v.unit}`;
const teamLabel = (t: TeamRef) => (t.archived ? `${t.name} (archived)` : t.name);

async function copy(text: string, toast: ReturnType<typeof useToast>, ok: string) {
  try { await navigator.clipboard.writeText(text); toast(ok); } catch { toast("Copy didn't work here. Select the text and copy it yourself.", "error"); }
}

export function EstimatePage() {
  const canSend = useCan("surveys.send");
  const canResults = useCan("surveys.results");
  const canAnswer = useCan("surveys.answer");
  if (!canSend && !canResults) {
    if (canAnswer) return <PageShell><PageHeader title="Team estimates" subtitle="Surveys sent to you. Your answers are anonymous." /><YourSurveys /></PageShell>;
    return (
      <PageShell>
        <PageHeader title="Team estimates" />
        <InlineAlert variant="info" title="You don't have access to team estimates. Ask an owner, controller or winemaker." />
      </PageShell>
    );
  }
  return <Organizer canSend={canSend} canResults={canResults} />;
}

/* ------------------------------------------------------------------ Your surveys */

function YourSurveys({ compact }: { compact?: boolean }) {
  const mine = useQuery({ queryKey: ["surveys-mine"], queryFn: () => api<{ surveys: { id: string; name: string; team: string; closesOn: string; answered: boolean }[] }>("/surveys/mine") });
  if (compact && !mine.data?.surveys.length) return null;
  return (
    <Card title="Your surveys" description="Surveys sent to you. Nobody sees who gave which answer.">
      <div className="card__c">
        {mine.isPending ? <div className="skel" aria-busy="true" /> : mine.isError ? (
          <InlineAlert variant="error" title="Surveys couldn't load. Try again in a minute; nothing was changed." />
        ) : !mine.data.surveys.length ? (
          <p className="muted m0">No surveys for you right now. When someone sends you one, it shows here.</p>
        ) : (
          <ul className="mine">
            {mine.data.surveys.map(s => (
              <li key={s.id}>
                <div><b>{s.name}</b><span className="small muted">{s.team} · closes {short(s.closesOn)}</span>
                  <span className="small">{s.answered ? `Answered. You can change your answers until ${short(s.closesOn)}.` : "Not answered yet"}</span></div>
                <a className="btn btn--primary btn--touch" href={`/survey?mine=${s.id}`}>Answer</a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ organizer */

function Organizer({ canSend, canResults }: { canSend: boolean; canResults: boolean }) {
  const canTeams = useCan("admin.manage");
  const qc = useQueryClient();
  const toast = useToast();
  const [, navigate] = useLocation();
  const selected = new URLSearchParams(useSearch()).get("survey");
  const list = useQuery({ queryKey: ["surveys"], queryFn: () => api<ListDto>("/surveys") });
  const [teamsOpen, setTeamsOpen] = useState(false);
  const teamsBtn = useRef<HTMLButtonElement>(null);
  const [focusName, setFocusName] = useState(false);

  const current = selected ?? list.data?.surveys[0]?.id ?? null;
  const select = (id: string) => navigate(`/estimate?survey=${id}`, { replace: true });

  const create = useMutation({
    mutationFn: () => api<{ survey: SurveyDto }>("/surveys", { method: "POST", body: {} }),
    onSuccess: d => { qc.setQueryData(["survey", d.survey.id], d); void qc.invalidateQueries({ queryKey: ["surveys"] }); setFocusName(true); select(d.survey.id); },
    onError: e => toast(errText(e), "error"),
  });
  const sync = useMutation({
    mutationFn: () => api<{ added: number; message: string }>("/rounds/sync-from-process", { method: "POST", body: {} }),
    onSuccess: d => { toast(d.message); void qc.invalidateQueries({ queryKey: ["surveys"] }); void qc.invalidateQueries({ queryKey: ["survey"] }); },
    onError: e => toast(errText(e), "error"),
  });

  return (
    <PageShell>
      <PageHeader title="Team estimates"
        subtitle="Every labor activity in the process gets an estimate round. Each person gets a personal link and gives hours for a small, a medium and a large job, anonymously. Each size's median becomes the starting hours in plans."
        actions={canSend ? (
          <div className="actions">
            <button type="button" className="btn" onClick={() => sync.mutate()} disabled={sync.isPending}>Check the process for new activities</button>
            {canTeams && <button type="button" className="btn" ref={teamsBtn} onClick={() => setTeamsOpen(true)}>Manage teams</button>}
            <button type="button" className="btn btn--primary" onClick={() => create.mutate()} disabled={create.isPending}>{icons.plus}New survey</button>
          </div>
        ) : canTeams ? <button type="button" className="btn" onClick={() => setTeamsOpen(true)}>Manage teams</button> : undefined} />
      <YourSurveys compact />
      {list.isError ? (
        <Card><EmptyState icon={icons.warning} title="Surveys couldn't load. Try again in a minute; nothing was changed." action={<button type="button" className="btn" onClick={() => void list.refetch()}>Retry</button>} /></Card>
      ) : (
        <div className="estgrid">
          <nav className="card" aria-label="Surveys">
            <div className="card__h"><div>
              <h2 className="card__t">Surveys <Tip term="Surveys" /></h2>
              {list.data && <p className="card__d">{plural(list.data.roundsTotal, "estimate round", "estimate rounds")}, one per labor activity in the process.</p>}
            </div></div>
            <div className="card__c roundlist">
              {list.isPending ? <><div className="skel" /><div className="skel" /><div className="skel" /></> : !list.data.surveys.length ? (
                <>
                  <p className="m0">No surveys yet. A survey asks one team how long its work takes. Start one with New survey.</p>
                  {!list.data.roundsTotal && <p className="small muted m0">Add a labor pool to an activity in the <Link href="/process">Process editor</Link>, and it gets an estimate round here.</p>}
                </>
              ) : list.data.surveys.map(s => (
                <button key={s.id} type="button" className="roundbtn" aria-current={s.id === current ? "true" : undefined} onClick={() => select(s.id)}>
                  <span className="stack"><b>{s.name}</b><span className="small muted">{plural(s.rounds, "round", "rounds")} · {teamLabel(s.team)}</span></span>
                  <StatusChip tone={STATUS[s.status]!.tone} quiet={s.status !== "open"}>{STATUS[s.status]!.label}</StatusChip>
                </button>
              ))}
              {!!list.data?.unassignedRounds && <p className="small muted m0">{plural(list.data.unassignedRounds, "round", "rounds")} not in any survey yet.</p>}
            </div>
          </nav>
          <div className="stackgap minw0">
            {current ? <SurveyView key={current} id={current} canSend={canSend} canResults={canResults} focusName={focusName} onFocused={() => setFocusName(false)} onAskedAgain={sid => { setFocusName(true); select(sid); }} />
              : list.isPending ? <div className="card skel skel--card" aria-busy="true" /> : null}
          </div>
        </div>
      )}
      {canTeams && <TeamsDialog open={teamsOpen} onClose={() => { setTeamsOpen(false); teamsBtn.current?.focus(); }} />}
    </PageShell>
  );
}

/* ------------------------------------------------------------------ one survey */

function SurveyView({ id, canSend, canResults, focusName, onFocused, onAskedAgain }: { id: string; canSend: boolean; canResults: boolean; focusName: boolean; onFocused: () => void; onAskedAgain: (id: string) => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ["survey", id], queryFn: () => api<{ survey: SurveyDto }>(`/surveys/${id}`) });
  const s = q.data?.survey;
  const [tab, setTab] = useState<"results" | "setup" | null>(null);
  const [reopening, setReopening] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["survey", id] }); void qc.invalidateQueries({ queryKey: ["surveys"] }); void qc.invalidateQueries({ queryKey: ["results", id] }); };
  const act = useMutation({
    mutationFn: ({ path, body }: { path: string; body?: unknown }) => api<Record<string, unknown>>(`/surveys/${id}/${path}`, { method: "POST", body: body ?? {} }),
    onSettled: refresh,
  });
  /** Runs one survey action; `ok` turns its response into the toast. */
  const run = (path: string, ok: ((d: Record<string, unknown>) => string) | null, opts: { body?: unknown; then?: (d: Record<string, unknown>) => void; failed?: (e: unknown) => void } = {}) =>
    act.mutate({ path, body: opts.body }, {
      onSuccess: d => { if (ok) toast(ok(d)); opts.then?.(d); },
      onError: e => { toast(errText(e), "error"); opts.failed?.(e); },
    });
  const num = (v: unknown) => Number(v ?? 0);

  if (q.isError) return <Card><EmptyState icon={icons.warning} title="Surveys couldn't load. Try again in a minute; nothing was changed." action={<button type="button" className="btn" onClick={() => void q.refetch()}>Retry</button>} /></Card>;
  if (!s) return <div className="card skel skel--card" aria-busy="true" />;
  const showResults = canResults;
  const current = tab ?? (s.status === "draft" || !showResults ? "setup" : "results");
  const closed = s.status === "closed" || s.status === "applied";
  const statusLine = s.status === "draft" ? `Draft. Closes ${short(s.closesOn)} once sent.`
    : s.status === "open" ? `Sent ${short(s.sentAt)} to ${plural(s.invited, "person", "people")} · ${s.answered} answered · closes ${short(s.closesOn)}`
      : s.status === "applied" ? `Applied ${short(s.appliedAt)}. Answers are final.` : `Closed ${short(s.closedOn ?? s.closedAt)} · ${s.answered} of ${s.invited} answered`;

  return (
    <>
      <div className="svhead">
        <div>
          <p className="eyebrow">{teamLabel(s.team)} · {plural(s.rounds.length, "round", "rounds")}</p>
          <h2 className="svhead__t">{s.name}</h2>
          <p className="small muted m0">{statusLine}</p>
          {s.status === "open" && <p className="small muted m0">{s.remindersEnabled ? (s.nextReminder ? `Reminders on. Next: ${short(s.nextReminder)}.` : "Reminders on.") : "Reminders off."}</p>}
        </div>
        {canSend && (
          <div className="actions">
            {s.status === "draft" && <a className="btn btn--sm" href={`/survey?preview=${s.id}`} target="_blank" rel="noopener">Preview in a new window</a>}
            {s.status === "draft" && <button type="button" className="btn btn--primary btn--sm" disabled={act.isPending}
              onClick={() => run("send", d => `Survey is open. ${plural(num(d.emailed), "email", "emails")} on their way.${num(d.toPrint) ? ` Print the sheet for ${plural(num(d.toPrint), "person", "people")} without email.` : ""}`,
                { failed: e => { if (e instanceof ApiError && e.code === "no_recipients") setTab("setup"); } })}>Send survey</button>}
            {s.status === "open" && <>
              <a className="btn btn--sm" href={`/api/surveys/${s.id}/sheet?who=no_email`} target="_blank" rel="noopener">Print sheet</a>
              <button type="button" className="btn btn--sm" disabled={act.isPending} onClick={() => run("remind", d => num(d.reminded) ? `Reminder sent to ${plural(num(d.reminded), "person", "people")}.` : "Everyone who hasn't answered got a reminder today. Try again tomorrow.")}>Remind now</button>
              <button type="button" className="btn btn--primary btn--sm" disabled={act.isPending} onClick={() => run("close", () => "Closed. Results are visible.", { then: () => setTab(null) })}>Close and show results</button>
            </>}
            {s.status === "closed" && <button type="button" className="btn btn--sm" onClick={() => setReopening(true)}>Reopen</button>}
            {closed && <button type="button" className="btn btn--sm" disabled={act.isPending}
              onClick={() => run("ask-again", null, { then: d => onAskedAgain((d.survey as { id: string }).id) })}>{s.status === "applied" ? "Ask again in a new survey" : "Ask again"}</button>}
          </div>
        )}
      </div>
      {reopening && <Reopen today={s.today} onCancel={() => setReopening(false)} onDone={closesOn => run("reopen", () => `Reopened. It closes ${short(closesOn)}.`, { body: { closesOn }, then: () => setReopening(false) })} />}
      {s.status === "open" && s.failed.count > 0 && !dismissed && (
        <div className="svalert" role="alert">
          <span>{`The survey invitations didn't all go out. ${s.failed.count} of ${s.failed.of} failed to send. Select Send again; people who got one won't get a second.`}</span>
          <span className="actions"><SendAgain id={s.id} onDone={refresh} /><button type="button" className="btn btn--sm btn--ghost" onClick={() => setDismissed(true)}>Dismiss</button></span>
        </div>
      )}
      {s.status === "open" && s.bounced.map(b => (
        <div className="svalert" role="alert" key={b.linkId}><span>{b.message}</span>{s.failed.count === 0 && <span className="actions"><SendAgain id={s.id} onDone={refresh} /></span>}</div>
      ))}
      {showResults ? (
        <div className="pagetabs pagetabs--sm" role="tablist" aria-label="Survey">
          {(["results", "setup"] as const).map(k => (
            <button key={k} type="button" role="tab" id={`stab-${k}`} className="pagetabs__tab" aria-selected={current === k} aria-controls="surveyPanel" tabIndex={current === k ? 0 : -1}
              onClick={() => setTab(k)} onKeyDown={e => {
                const n = e.key === "ArrowRight" || e.key === "ArrowLeft" ? (k === "results" ? "setup" : "results") : e.key === "Home" ? "results" : e.key === "End" ? "setup" : null;
                if (n) { e.preventDefault(); setTab(n); document.getElementById(`stab-${n}`)?.focus(); }
              }}>
              {k === "results" ? "Results" : "Set up and send"}
            </button>
          ))}
        </div>
      ) : <p className="small muted m0">Results are shown to owners, controllers and winemakers.</p>}
      <div id="surveyPanel" role={showResults ? "tabpanel" : undefined} aria-labelledby={showResults ? `stab-${current}` : undefined} className="stackgap">
        {current === "setup" ? <Setup s={s} canSend={canSend} focusName={focusName} onFocused={onFocused} onSaved={refresh} showPeople={!(showResults && s.status === "open")} />
          : !closed ? <><Blind s={s} onSetup={() => { setTab("setup"); document.getElementById("stab-setup")?.focus(); }} />{s.status === "open" && <People s={s} onDone={refresh} />}</> : <Results s={s} />}
      </div>
    </>
  );
}

function Reopen({ today, onCancel, onDone }: { today: string; onCancel: () => void; onDone: (d: string) => void }) {
  const [date, setDate] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const dateRef = useRef<HTMLInputElement>(null);
  useEffect(() => { dateRef.current?.focus(); }, []); // the form opens in place, not in a Dialog, so it moves focus itself
  return (
    <form className="card card__c reopen" onSubmit={e => { e.preventDefault(); if (!date || date <= today) { setErr("Choose a closing date after today."); return; } onDone(date); }}>
      <Field label="New closing date" error={err}><input className="input" type="date" min={today} value={date} onChange={e => setDate(e.target.value)} ref={dateRef} /></Field>
      <div className="actions"><button type="submit" className="btn btn--primary btn--sm">Reopen</button><button type="button" className="btn btn--sm" onClick={onCancel}>Cancel</button></div>
    </form>
  );
}

function SendAgain({ id, onDone }: { id: string; onDone: () => void }) {
  const toast = useToast();
  const m = useMutation({
    mutationFn: () => api<{ requeued: number; refused: { address: string }[] }>(`/surveys/${id}/send-again`, { method: "POST", body: {} }),
    onSuccess: d => {
      const refused = d.refused.map(r => r.address);
      const more = refused.length > 3 ? ` and ${refused.length - 3} more` : "";
      toast(`Sending again to ${plural(d.requeued, "person", "people")}.${refused.length ? ` Not sent to ${refused.slice(0, 3).join(", ")}${more}: change it first.` : ""}`);
      onDone();
    },
    onError: e => { toast(errText(e), "error"); onDone(); },
  });
  return <button type="button" className="btn btn--sm" onClick={() => m.mutate()} disabled={m.isPending}>Send again</button>;
}

/* ------------------------------------------------------------------ Set up and send */

const peopleText = (p: PersonDto[] | undefined) => (p ?? []).map(x => (x.email ? `${x.name}, ${x.email}` : x.name)).join("\n");
function parsePeople(text: string, before: PersonDto[]) {
  const used = new Set<string>();
  return text.split("\n").map(l => l.trim()).filter(Boolean).map(l => {
    const at = l.lastIndexOf(",");
    const tail = at >= 0 ? l.slice(at + 1).trim() : "";
    const looksEmail = tail.includes("@") || (at >= 0 && tail && !tail.includes(" "));
    const name = looksEmail ? l.slice(0, at).trim() : l;
    const email = looksEmail ? tail : null;
    // keep each person's link when their row is still there (an email change then sends a fresh link)
    const same = before.find(b => !used.has(b.id) && b.name === name) ?? before.find(b => !used.has(b.id) && email && b.email?.toLowerCase() === email.toLowerCase());
    if (same) used.add(same.id);
    return { ...(same ? { id: same.id } : {}), name, email, userId: same?.userId ?? null };
  });
}

function Setup({ s, canSend, focusName, onFocused, onSaved, showPeople }: { s: SurveyDto; canSend: boolean; focusName: boolean; onFocused: () => void; onSaved: () => void; showPeople: boolean }) {
  const toast = useToast();
  const session = useSession();
  const teams = useQuery({ queryKey: ["survey-teams", false], queryFn: () => api<{ teams: TeamDto[] }>("/survey-teams") });
  const [name, setName] = useState(s.name);
  const [teamId, setTeamId] = useState(s.team.id);
  const [intro, setIntro] = useState(s.intro);
  const [closesOn, setClosesOn] = useState(s.closesOn);
  const [people, setPeople] = useState(peopleText(s.people));
  const [reminders, setReminders] = useState(s.remindersEnabled);
  const [roundIds, setRoundIds] = useState<string[]>(s.rounds.map(r => r.id));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const nameRef = useRef<HTMLInputElement>(null);
  const draft = s.status === "draft";
  const locked = !draft || !canSend;
  const readOnly = !canSend || s.status === "closed" || s.status === "applied";

  useEffect(() => { if (focusName && nameRef.current) { nameRef.current.focus(); nameRef.current.select(); onFocused(); } }, [focusName, onFocused]);

  const save = useMutation({
    mutationFn: () => api<{ survey: SurveyDto }>(`/surveys/${s.id}`, { method: "PATCH", body: { revision: s.revision, name, teamId, intro, closesOn, remindersEnabled: reminders, people: parsePeople(people, s.people ?? []), roundIds } }),
    onSuccess: () => { setErrors({}); toast("Survey saved."); onSaved(); },
    onError: e => {
      if (e instanceof ApiError && typeof e.details.field === "string") setErrors({ [e.details.field]: e.message });
      else { setErrors({ form: errText(e) }); if (e instanceof ApiError && e.code === "edit_conflict") onSaved(); }
    },
  });

  const rounds = draft ? s.availableRounds : s.rounds;
  const byStage = useMemo(() => {
    const m = new Map<string, RoundView[]>();
    for (const r of rounds) { const k = r.stage ?? "warehouse"; m.set(k, [...(m.get(k) ?? []), r]); }
    return [...m.entries()].sort((a, b) => STAGE_ORDER.indexOf(a[0]) - STAGE_ORDER.indexOf(b[0]));
  }, [rounds]);
  const activeTeams = (teams.data?.teams ?? []).map(t => ({ id: t.id, name: t.name, archived: false }));
  const teamOptions = s.team.archived ? [s.team, ...activeTeams] : activeTeams.length ? activeTeams : [s.team];
  const sender = session.data?.user?.name ?? "";
  const n = roundIds.length;
  const firstLink = s.people?.find(p => p.link)?.link ?? null;
  const emailBody = `Hi,\n\nI'm asking ${teamLabel(s.team)} for a few quick estimates of how long our work takes. ${intro}\n\nIt's ${plural(n, "question", "questions")} and takes about ${Math.max(2, Math.round(n * 1.5))} minutes. Your answers are anonymous: nobody sees who said what, and results stay hidden until the survey closes on ${short(closesOn)}.\n\nStart here: ${firstLink ?? "(each person's own link, once the survey is sent)"}\n\nThank you,\n${sender}`;
  const subject = `${sender} asks: ${plural(n, "quick estimate", "quick estimates")} for ${session.data?.tenant?.name ?? "the winery"}`;

  const submit = (e: FormEvent) => { e.preventDefault(); if (!readOnly) save.mutate(); };
  return (
    <>
      <form className="card" onSubmit={submit} noValidate>
        <div className="card__h"><div><h3 className="card__t">Survey</h3><p className="card__d">What the team sees first, and who gets it. Each person gets their own link.</p></div></div>
        <div className="card__c fields fields--2">
          <Field label="Survey name" error={errors.name}><input ref={nameRef} className="input" value={name} maxLength={80} disabled={readOnly} onChange={e => setName(e.target.value)} /></Field>
          <Field label="Team" error={errors.teamId}>
            <select className="select" value={teamId} disabled={readOnly} onChange={e => setTeamId(e.target.value)}>
              {teamOptions.map(t => <option key={t.id} value={t.id}>{teamLabel(t)}</option>)}
            </select>
          </Field>
          <Field label="Why we're asking (shown on the first page)" error={errors.intro} className="span2"><textarea className="input textarea" rows={3} value={intro} disabled={readOnly} onChange={e => setIntro(e.target.value)} /></Field>
          <Field label="Closes" error={errors.closesOn} hint="At 23:59 that day in the winery's time zone."><input className="input" type="date" min={s.today} value={closesOn} disabled={readOnly} onChange={e => setClosesOn(e.target.value)} /></Field>
          <Field label="People (one per line: name, then email if they have one)" error={errors.people} hint="People without an email get the printed sheet with a QR code.">
            <textarea className="input textarea" rows={4} value={people} disabled={readOnly} onChange={e => setPeople(e.target.value)} />
          </Field>
          <label className="toggle span2">
            <input type="checkbox" checked={reminders} disabled={readOnly} onChange={e => setReminders(e.target.checked)} />
            <span>Remind people who haven't answered (2 days before it closes, and on the closing day)</span>
          </label>
        </div>
        <div className="card__c">
          <fieldset className="roundpick">
            <legend className="card__t">Rounds in this survey <Tip term="Rounds" /></legend>
            <p className="small muted m0">One round per labor activity in the process map. Each round asks for hours at the activity's three sizes.</p>
            {!draft && <p className="small muted m0">Rounds and their sizes are locked once a survey is sent, so every answer is to the same questions.</p>}
            {!rounds.length && <p className="small muted m0">Add a labor pool to an activity in the <Link href="/process">Process editor</Link>, and it gets an estimate round here.</p>}
            {byStage.map(([stage, rs]) => (
              <div className="roundgroup" key={stage}>
                <span className="kind">{STAGE_NAME[stage] ?? stage}</span>
                {rs.map(r => (
                  <div className="roundpick__r" key={r.id}>
                    <label className="checkrow">
                      <input type="checkbox" checked={roundIds.includes(r.id)} disabled={locked} onChange={e => setRoundIds(ids => (e.target.checked ? [...ids, r.id] : ids.filter(x => x !== r.id)))} />
                      <b>{r.title}</b>
                    </label>
                    <span className="small muted">
                      {r.bands ? `Small ${r.bands.S.range} · Medium ${r.bands.M.range} · Large ${r.bands.L.range} ` : ""}
                      {r.sizesMissing && roundIds.includes(r.id) && <span className="field__error">Set the sizes for {r.title} before sending. </span>}
                      {draft && r.activityKey ? <Link href={`/templates?sizes=${encodeURIComponent(r.activityKey)}`}>Edit sizes</Link> : <span className="linkoff" aria-disabled="true">Edit sizes</span>}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </fieldset>
        </div>
        {errors.form && <div className="card__c"><InlineAlert variant="error" title={errors.form} /></div>}
        {!readOnly && <div className="card__c"><button type="submit" className="btn btn--primary" disabled={save.isPending}>Save survey</button></div>}
      </form>

      {showPeople && <People s={s} onDone={onSaved} />}

      <Card title="Invitation email" description="Sending emails each person their own link. Copy it only if you'd rather send it yourself."
        actions={<div className="actions"><button type="button" className="btn btn--sm" onClick={() => void copy(`Subject: ${subject}\n\n${emailBody}`, toast, "Invitation email copied.")}>Copy email</button>
          {firstLink && <button type="button" className="btn btn--sm" onClick={() => void copy(firstLink, toast, "Survey link copied.")}>Copy link only</button>}</div>}>
        <div className="card__c"><div className="emailprev">
          <p className="m0"><span className="muted">To:</span> {(s.people ?? []).filter(p => p.email).map(p => p.email).join(", ") || "Add people above"}</p>
          <p className="m0"><span className="muted">Subject:</span> {subject}</p>
          <pre>{emailBody}</pre>
        </div></div>
      </Card>
    </>
  );
}

function People({ s, onDone }: { s: SurveyDto; onDone: () => void }) {
  const hid = useId();
  if (s.status === "draft" || !s.people) return null;
  return (
    <Card title="People" headingId={hid} description="One personal link each. Reminders go to everyone with an email who hasn't answered, without saying who has.">
      <ul className="card__c peoplelist">
        {s.people.map(p => <PersonRow key={p.id} p={p} survey={s} onDone={onDone} />)}
      </ul>
    </Card>
  );
}

function PersonRow({ p, survey, onDone }: { p: PersonDto; survey: SurveyDto; onDone: () => void }) {
  const toast = useToast();
  const resend = useMutation({
    mutationFn: () => api<{ message: string }>(`/surveys/${survey.id}/links/${p.id}/resend`, { method: "POST", body: {} }),
    onSuccess: d => { toast(d.message); onDone(); },
    onError: e => toast(errText(e), "error"),
  });
  return (
    <li>
      <div className="stack"><b>{p.name}</b><span className="small muted">{p.email ?? "Printed sheet"}</span>{p.bounced && <span className="small field__error">{p.bounced}</span>}</div>
      <div className="actions">
        {p.link && <button type="button" className="btn btn--sm" onClick={() => void copy(p.link!, toast, "Survey link copied.")} aria-label={`Copy link for ${p.name}`}>Copy link</button>}
        {survey.status === "open" && <button type="button" className="btn btn--sm" disabled={resend.isPending} onClick={() => resend.mutate()} aria-label={`Resend link to ${p.name}`}>Resend link</button>}
      </div>
    </li>
  );
}

/* ------------------------------------------------------------------ Results */

function Blind({ s, onSetup }: { s: SurveyDto; onSetup: () => void }) {
  if (s.status === "draft") {
    return (
      <div className="blind">
        <span>This survey hasn't been sent yet. Use Set up and send.</span>
        <button type="button" className="btn btn--sm" onClick={onSetup}>Set up and send</button>
      </div>
    );
  }
  return (
    <>
      <div className="blind">
        <b>Answers stay hidden until the survey closes</b>
        <span>{s.answered} of {s.invited} {s.invited === 1 ? "person has" : "people have"} answered so far. Keeping results hidden means the first number nobody says out loud can't anchor the rest.</span>
      </div>
      <Card title="What the team will be asked">
        <ol className="card__c qlist">
          {s.rounds.map(r => (
            <li key={r.id}><b>{r.title}</b>{r.question && <span className="small muted">{r.question}</span>}
              {r.bands && <span className="small muted">Small {r.bands.S.range} · Medium {r.bands.M.range} · Large {r.bands.L.range}</span>}</li>
          ))}
        </ol>
      </Card>
    </>
  );
}

function Results({ s }: { s: SurveyDto }) {
  const canPlan = useCan("programs.plan");
  const qc = useQueryClient();
  const toast = useToast();
  const res = useQuery({ queryKey: ["results", s.id], queryFn: () => api<ResultsDto>(`/surveys/${s.id}/results`) });
  const targets = useQuery({ queryKey: ["apply-targets"], queryFn: () => api<TargetsDto>("/apply-targets"), enabled: canPlan });
  const [target, setTarget] = useState<string>("");
  const [allOpen, setAllOpen] = useState(false);
  const [choose, setChoose] = useState<{ roundId: string; message: string; rows: { id: string; role: string; hours: number | null }[] } | null>(null);
  const firstTarget = targets.data ? (targets.data.programs[0] ? `program:${targets.data.programs[0].id}` : targets.data.templates[0] ? `template:${targets.data.templates[0].id}` : "") : "";
  const t = target || firstTarget;
  const [kind, tid] = t ? (t.split(":") as ["program" | "template", string]) : [null, null];
  const preview = useQuery({
    queryKey: ["apply-preview", s.id, t],
    queryFn: () => api<PreviewDto>(`/surveys/${s.id}/apply-all`, { method: "POST", body: { target: { kind, id: tid }, preview: true } }),
    enabled: canPlan && !!t,
  });
  const done = () => { void qc.invalidateQueries({ queryKey: ["results", s.id] }); void qc.invalidateQueries({ queryKey: ["apply-preview", s.id] }); void qc.invalidateQueries({ queryKey: ["survey", s.id] }); void qc.invalidateQueries({ queryKey: ["surveys"] }); };
  const use = useMutation({
    mutationFn: ({ roundId, laborLineId }: { roundId: string; laborLineId?: string }) => api<{ message: string }>(`/rounds/${roundId}/apply`, { method: "POST", body: { target: { kind, id: tid }, ...(laborLineId ? { laborLineId } : {}) } }),
    onSuccess: d => { setChoose(null); toast(d.message); done(); },
    onError: (e, v) => {
      if (e instanceof ApiError && e.code === "choose_row") setChoose({ roundId: v.roundId, message: e.message, rows: (e.details.rows as { id: string; role: string; hours: number | null }[]) ?? [] });
      else { toast(errText(e), "error"); done(); }
    },
  });

  if (res.isError) return <Card><EmptyState icon={icons.warning} title="Surveys couldn't load. Try again in a minute; nothing was changed." action={<button type="button" className="btn" onClick={() => void res.refetch()}>Retry</button>} /></Card>;
  if (!res.data) return <div className="card skel skel--card" aria-busy="true" />;
  const targetName = preview.data?.target.name ?? "";
  const rowFor = (roundId: string) => preview.data?.rows.find(r => r.roundId === roundId);

  return (
    <>
      {canPlan && (
        <Card title="Apply to" description="One target at a time: a template, or a program that isn't in production yet. A program in production takes new medians through a re-plan in the Program builder.">
          <div className="card__c applyto">
            {targets.data && !targets.data.programs.length && !targets.data.templates.length ? <p className="small muted m0">No saved templates or programs in planning to apply to.</p> : (
              <>
                <label className="sr" htmlFor="applyTarget">Apply to</label>
                <select id="applyTarget" className="select" value={t} onChange={e => setTarget(e.target.value)}>
                  {!!targets.data?.programs.length && <optgroup label="Programs">{targets.data.programs.map(p => <option key={p.id} value={`program:${p.id}`}>{p.name}</option>)}</optgroup>}
                  {!!targets.data?.templates.length && <optgroup label="Templates">{targets.data.templates.map(p => <option key={p.id} value={`template:${p.id}`}>{p.name}</option>)}</optgroup>}
                </select>
                <button type="button" className="btn btn--primary" disabled={!preview.data} onClick={() => setAllOpen(true)}>Apply all</button>
              </>
            )}
            {preview.isError && <InlineAlert variant="warning" title={errText(preview.error)} />}
          </div>
        </Card>
      )}
      {res.data.rounds.map(r => <RoundCard key={r.id} r={r} preview={rowFor(r.id)} targetName={targetName} canApply={canPlan && !!preview.data} busy={use.isPending} onUse={() => use.mutate({ roundId: r.id })} />)}
      <Card title={`Notes from the team (${res.data.notes.length})`} description="Shown after the survey closed, without names or times.">
        <div className="card__c">
          {!res.data.notes.length ? <p className="small muted m0">No notes.</p> : (
            <ul className="notes">{res.data.notes.map((n, i) => <li key={i}><q>{n.body}</q><span className="small muted">{res.data.rounds.find(r => r.id === n.roundId)?.activity}</span></li>)}</ul>
          )}
        </div>
      </Card>
      {allOpen && preview.data && <ApplyAll survey={s} preview={preview.data} target={{ kind: kind!, id: tid! }} onClose={() => setAllOpen(false)} onDone={() => { setAllOpen(false); done(); }} onStale={() => void preview.refetch()} />}
      <Dialog title="Choose a row" open={!!choose} onClose={() => setChoose(null)}
        actions={choose && <ChooseActions choose={choose} onPick={id => use.mutate({ roundId: choose.roundId, laborLineId: id })} onCancel={() => setChoose(null)} busy={use.isPending} />}>
        {choose && <p className="m0">{choose.message}</p>}
      </Dialog>
    </>
  );
}

function ChooseActions({ choose, onPick, onCancel, busy }: { choose: { rows: { id: string; role: string; hours: number | null }[] }; onPick: (id: string) => void; onCancel: () => void; busy: boolean }) {
  const [id, setId] = useState(choose.rows[0]?.id ?? "");
  return (
    <div className="choose">
      <fieldset className="stack">
        <legend className="sr">Row</legend>
        {choose.rows.map(r => <label className="checkrow" key={r.id}><input type="radio" name="chooseRow" checked={id === r.id} onChange={() => setId(r.id)} />{r.role}{r.hours != null && <span className="small muted"> · now {h1(r.hours)} h</span>}</label>)}
      </fieldset>
      <div className="actions"><button type="button" className="btn" onClick={onCancel}>Cancel</button><button type="button" className="btn btn--primary" disabled={busy || !id} onClick={() => onPick(id)}>Use these hours</button></div>
    </div>
  );
}

function RoundCard({ r, preview, targetName, canApply, busy, onUse }: { r: ResultsDto["rounds"][number]; preview?: PreviewRow; targetName: string; canApply: boolean; busy: boolean; onUse: () => void }) {
  const total = Math.max(...SIZES.map(z => r.bands[z].answers + r.bands[z].notSure));
  const none = SIZES.every(z => r.bands[z].answers === 0);
  return (
    <section className="card rcard" aria-labelledby={`rc-${r.id}`}>
      <div className="card__h"><div>
        <h3 className="card__t" id={`rc-${r.id}`}>{r.activity}</h3>
        <p className="card__d">{r.hoursPer === "month" ? "Hours each month" : "Hours for the whole job"} · {plural(total, "person", "people")} answered</p>
      </div></div>
      <div className="card__c stackgap">
        {r.bandsChanged && <InlineAlert variant="warning" title={`The sizes for ${r.activity} changed after this survey was sent, so these answers are for the old sizes. Ask again in a new survey.`} />}
        {none ? <p className="m0">No answers.</p> : SIZES.map(z => {
          const b = r.bands[z];
          const match = canApply && !r.bandsChanged && preview && !preview.skipped && preview.size === z;
          return (
            <div className="szrow" key={z}>
              <p className="szrow__h">
                <b>{SIZE_NAME[z]}{b.range && ` (${b.range})`}</b>
                <span className="small muted"> · {plural(b.answers, "answer", "answers")}{b.notSure ? `, ${b.notSure} not sure` : ""}{b.median != null ? ` · median ${h1(b.median)} h` : ""}</span>
                {b.median != null && (b.wideSpread ? <><StatusChip tone="warning">Wide spread</StatusChip> <Tip term="Wide spread" /></> : <StatusChip tone="success" quiet>Team agrees</StatusChip>)}
              </p>
              {b.answers === 0 ? <p className="small muted m0">No answers for this size.</p> : <DotPlot b={b} />}
              {b.wideSpread && b.outliers.length > 0 && <InlineAlert variant="warning" title="One answer is far from the rest. Worth a five-minute conversation, then a short time study for this activity." />}
              {match && preview!.hoursAfter != null && (
                <div className="szrow__use">
                  <p className="small m0">{targetName} is {SIZE_NAME[z]} for this activity{preview!.volume ? ` (${volumeText(preview!.volume)})` : ""}. Use {h1(preview!.hoursAfter)} hours{preview!.hoursBefore != null ? ` (plan now ${h1(preview!.hoursBefore)})` : ""}.</p>
                  <button type="button" className="btn btn--sm" disabled={busy} onClick={onUse}>Use {h1(preview!.hoursAfter)} hours for {targetName}</button>
                </div>
              )}
            </div>
          );
        })}
        {canApply && preview?.skipped === "no_matching_work" && <p className="small muted m0">{targetName} has no {r.activity} work to apply this to.</p>}
        {r.appliedTo.map((a, i) => <p className="small m0" key={i}>Applied to {a.name} on {short(a.on)}.</p>)}
      </div>
    </section>
  );
}

/**
 * Each answer as its own dot from the lowest to the highest (tied answers stack), the median as a line
 * across the plot, and the lowest, median and highest answers under it. The label carries the same
 * numbers, and names the answers far from the rest (they're marked by colour on screen).
 */
// dot plot geometry: 10px dots, each tie stacks 12px higher, the axis 12px off the bottom
const DOT_STEP = 12;
const leftAt = (pct: number) => ({ left: `${pct}%` });
const dotAt = (pct: number, level: number) => ({ left: `${pct}%`, bottom: `${7 + level * DOT_STEP}px` });
const plotHeight = (levels: number) => ({ height: `${36 + levels * DOT_STEP}px` });

function DotPlot({ b }: { b: BandResult }) {
  const dots = dotLayout(b.hours);
  const lo = dots[0]?.hours ?? 0;
  const hi = dots[dots.length - 1]?.hours ?? lo;
  const at = (h: number) => (hi === lo ? 50 : ((h - lo) / (hi - lo)) * 100);
  const levels = Math.max(0, ...dots.map(d => d.level));
  const far = [...new Set(dots.filter(d => b.outliers.includes(d.hours)).map(d => d.hours))];
  const med = b.median != null ? at(b.median) : null;
  // the median's number shows under its line unless it would sit on the lowest or highest number
  const medLabel = med != null && med > 12 && med < 88;
  const label = `Answers: ${dots.map(d => `${fmtH(d.hours)} h`).join(", ")}. Median ${b.median != null ? h1(b.median) : "none"} h.${far.length ? ` Far from the rest: ${far.map(h => `${fmtH(h)} h`).join(", ")}.` : ""}`;
  return (
    <div className="dots" role="img" aria-label={label}>
      <div className="dots__plot" style={plotHeight(levels)} aria-hidden="true">
        {med != null && <span className="dots__med" style={leftAt(med)} />}
        {dots.map((d, i) => <span key={i} className={far.includes(d.hours) ? "dots__d dots__d--out" : "dots__d"} style={dotAt(d.x, d.level)} />)}
      </div>
      <div className="dots__scale" aria-hidden="true">
        <span className="dots__lo">{fmtH(lo)}</span>
        {medLabel && <span className="dots__mid" style={leftAt(med)}>{h1(b.median!)}</span>}
        <span className="dots__hi">{fmtH(hi)}</span>
      </div>
    </div>
  );
}

const fmtH = (h: number) => (Number.isInteger(h) ? String(h) : String(Math.round(h * 100) / 100));

function ApplyAll({ survey, preview, target, onClose, onDone, onStale }: { survey: SurveyDto; preview: PreviewDto; target: { kind: string; id: string }; onClose: () => void; onDone: () => void; onStale: () => void }) {
  const toast = useToast();
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);
  const live = preview.rows.filter(r => !r.skipped);
  const m = useMutation({
    mutationFn: () => api<{ applied: number; message: string }>(`/surveys/${survey.id}/apply-all`, { method: "POST", body: { target, previewRevision: preview.previewRevision, rowChoices: choices } }),
    onSuccess: d => { toast(d.message); onDone(); },
    onError: e => { setErr(errText(e)); if (e instanceof ApiError && e.code === "edit_conflict") onStale(); },
  });
  const skipWord: Record<string, string> = { no_matching_work: "No matching work", no_median: "No answers for this size", bands_changed: "Sizes changed" };
  const tid = useId();
  return (
    <Dialog title={`Apply to ${preview.target.name}`} open onClose={onClose}
      actions={<div className="actions"><button type="button" className="btn" onClick={onClose}>Cancel</button><button type="button" className="btn btn--primary" disabled={m.isPending || !live.length} onClick={() => m.mutate()}>Apply {plural(live.length, "change", "changes")}</button></div>}>
      <div className="tablewrap">
        <table className="table" aria-describedby={tid}>
          <thead><tr><th scope="col">Activity</th><th scope="col">Size</th><th scope="col">People row</th><th scope="col" className="num">Hours now</th><th scope="col" className="num">Hours after</th></tr></thead>
          <tbody>
            {preview.rows.map(r => (
              <tr key={r.roundId}>
                <th scope="row">{r.activity}</th>
                {r.skipped ? <td colSpan={4} className="muted">{skipWord[r.skipped] ?? r.skipped}: skipped</td> : <>
                  <td>{r.size ? SIZE_NAME[r.size] : ""}</td>
                  <td>{r.rows && r.rows.length > 1 ? (
                    <select className="select select--sm" aria-label={`People row for ${r.activity}`} value={choices[r.roundId] ?? r.line?.id} onChange={e => setChoices(c => ({ ...c, [r.roundId]: e.target.value }))}>
                      {r.rows.map(x => <option key={x.id} value={x.id}>{x.role}</option>)}
                    </select>
                  ) : r.line?.role}</td>
                  <td className="num">{r.hoursBefore != null ? h1(r.hoursBefore) : "—"}</td>
                  <td className="num">{r.hoursAfter != null ? h1(r.hoursAfter) : ""}</td>
                </>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="small muted" id={tid}>Hours only; the plan reprices after you apply.</p>
      {err && <InlineAlert variant="error" title={err} />}
    </Dialog>
  );
}

/* ------------------------------------------------------------------ Teams (H19) */

function TeamsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const active = useQuery({ queryKey: ["survey-teams", false], queryFn: () => api<{ teams: TeamDto[] }>("/survey-teams"), enabled: open });
  const archived = useQuery({ queryKey: ["survey-teams", true], queryFn: () => api<{ teams: TeamDto[] }>("/survey-teams?archived=true"), enabled: open });
  const [name, setName] = useState("");
  const [addErr, setAddErr] = useState<string | null>(null);
  const addRef = useRef<HTMLInputElement>(null);
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["survey-teams"] }); void qc.invalidateQueries({ queryKey: ["surveys"] }); void qc.invalidateQueries({ queryKey: ["survey"] }); };
  const add = useMutation({
    mutationFn: () => api<{ team: TeamDto }>("/survey-teams", { method: "POST", body: { name } }),
    onSuccess: d => { toast(`Added ${d.team.name}.`); setName(""); setAddErr(null); refresh(); addRef.current?.focus(); },
    onError: e => setAddErr(errText(e)),
  });
  const teams = active.data?.teams ?? [];
  const full = teams.length >= 20;
  return (
    <Dialog title="Teams" open={open} onClose={onClose} actions={<button type="button" className="btn" onClick={onClose}>Close</button>}>
      <p className="small muted">The teams you send surveys to. Archived teams keep their past surveys and answers.</p>
      {active.isPending ? <div className="skel" /> : active.isError ? <InlineAlert variant="error" title="Teams couldn't load. Try again in a minute; nothing was changed." /> : (
        <ul className="teams">{teams.map(t => <TeamRow key={t.id} t={t} onDone={refresh} />)}</ul>
      )}
      <form className="teams__add" onSubmit={e => { e.preventDefault(); add.mutate(); }} noValidate>
        <Field label="Add a team" error={addErr} hint={full ? "You have 20 teams, the most there can be. Archive one you don't use to add another." : undefined}>
          <input ref={addRef} className="input input--touch" value={name} disabled={full} onChange={e => setName(e.target.value)} />
        </Field>
        <button type="submit" className="btn btn--touch" disabled={full || add.isPending}>Add</button>
      </form>
      {!!archived.data?.teams.length && (
        <details className="teams__arch">
          <summary>Archived ({archived.data.teams.length})</summary>
          <ul className="teams">{archived.data.teams.map(t => <li key={t.id}><span>{t.name}</span><span className="small muted">Archived {short(t.archivedAt)}</span></li>)}</ul>
        </details>
      )}
    </Dialog>
  );
}

function TeamRow({ t, onDone }: { t: TeamDto; onDone: () => void }) {
  const toast = useToast();
  const [value, setValue] = useState(t.name);
  const [err, setErr] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);
  useEffect(() => { setValue(t.name); }, [t.name]);
  useEffect(() => { if (asking) cancelRef.current?.focus(); }, [asking]);
  const rename = useMutation({
    mutationFn: () => api<{ team: TeamDto }>(`/survey-teams/${t.id}`, { method: "PATCH", body: { name: value, version: t.version } }),
    onSuccess: d => { setErr(null); toast(`Renamed to ${d.team.name}.`); onDone(); },
    onError: e => { setErr(errText(e)); if (e instanceof ApiError && e.code === "edit_conflict") onDone(); },
  });
  const archive = useMutation({
    mutationFn: () => api<{ team: TeamDto }>(`/survey-teams/${t.id}/archive`, { method: "POST", body: { version: t.version } }),
    onSuccess: () => { setAsking(false); toast(`Archived ${t.name}. Its past surveys keep it.`); onDone(); },
    onError: e => { setAsking(false); setErr(errText(e)); },
  });
  const save = () => { if (value.trim() !== t.name) rename.mutate(); };
  return (
    <li>
      <div className="teams__row">
        <div className="teams__name">
          <label className="sr" htmlFor={`team-${t.id}`}>Team name</label>
          <input id={`team-${t.id}`} className="input input--touch" value={value} aria-invalid={err ? true : undefined} aria-describedby={err ? `team-${t.id}-err` : undefined}
            onChange={e => setValue(e.target.value)} onBlur={save} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); save(); } }} />
          <span className="small muted">{plural(t.surveys, "survey", "surveys")}</span>
        </div>
        {asking ? (
          <span className="actions" onKeyDown={e => { if (e.key === "Escape") { e.stopPropagation(); e.preventDefault(); setAsking(false); } }}>
            <span className="small">Archive {t.name}?</span>
            <button type="button" className="btn btn--sm btn--destructive" onClick={() => archive.mutate()} disabled={archive.isPending}>Archive</button>
            <button type="button" className="btn btn--sm" ref={cancelRef} onClick={() => setAsking(false)}>Cancel</button>
          </span>
        ) : <button type="button" className="btn btn--touch" onClick={() => { setErr(null); setAsking(true); }} aria-label={`Archive ${t.name}`}>Archive</button>}
      </div>
      {err && <p className="field__error m0" id={`team-${t.id}-err`}>{err}</p>}
    </li>
  );
}

